/**
 * AuditLogDetailsModal Component
 *
 * Modal displaying full details of an audit log entry.
 */

import React from "react";
import { Modal } from "../../../components/ui/Modal";
import { Button } from "../../../components/ui/Button";
import { ActionBadge, OutcomeBadge } from "./AuditLogActionBadge";
import type { AuditLogEntry } from "../../../types/audit-log.types";

interface AuditLogDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  entry: AuditLogEntry;
}

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="py-2 sm:grid sm:grid-cols-3 sm:gap-4">
    <dt className="text-sm font-medium text-gray-500">{label}</dt>
    <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">{children}</dd>
  </div>
);

export const AuditLogDetailsModal: React.FC<AuditLogDetailsModalProps> = ({ isOpen, onClose, entry }) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatResourceType = (type: string) => {
    return type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <Modal.Header>Audit Log Details</Modal.Header>
      <Modal.Body>
        <div className="space-y-6">
          {/* Overview */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Overview</h3>
            <dl className="border-t border-gray-200">
              <DetailRow label="Audit ID">
                <span className="font-mono text-sm">{entry.auditId}</span>
              </DetailRow>
              <DetailRow label="Timestamp">{formatDate(entry.createdAt)}</DetailRow>
              <DetailRow label="Action">
                <ActionBadge action={entry.action} />
              </DetailRow>
              <DetailRow label="Outcome">
                <OutcomeBadge outcome={entry.outcome} />
              </DetailRow>
            </dl>
          </div>

          {/* Actor */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Actor</h3>
            <dl className="border-t border-gray-200">
              <DetailRow label="Email">{entry.actorEmail}</DetailRow>
              <DetailRow label="User ID">
                <span className="font-mono text-sm">{entry.actorId}</span>
              </DetailRow>
            </dl>
          </div>

          {/* Resource */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Resource</h3>
            <dl className="border-t border-gray-200">
              <DetailRow label="Type">{formatResourceType(entry.resourceType)}</DetailRow>
              <DetailRow label="Resource ID">
                <span className="font-mono text-sm">{entry.resourceId}</span>
              </DetailRow>
            </dl>
          </div>

          {/* Request */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Request</h3>
            <dl className="border-t border-gray-200">
              <DetailRow label="HTTP Method">
                <span className="font-mono text-sm font-medium">{entry.httpMethod}</span>
              </DetailRow>
              <DetailRow label="Route Path">
                <span className="font-mono text-sm">{entry.routePath}</span>
              </DetailRow>
              <DetailRow label="Status Code">
                <span className={`font-mono text-sm font-medium ${entry.statusCode >= 400 ? "text-red-600" : "text-green-600"}`}>
                  {entry.statusCode}
                </span>
              </DetailRow>
            </dl>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end pt-4 border-t">
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};
